import React from 'react';
import { SearchDropdown } from '../../../../../components';
import { NewExistingSegment, NewExistingValue } from '../../../../../components/shared';
import { UTILITY_CONFIG } from '../../../../../constants/app.constants';
import { NewExistingType } from '../../../../../types';

interface UtilitySelectorProps {
  onServiceFeedSourceSelect: (value: string) => void;
  onDistanceFromUmCalloutChange: (value: string) => void;
  onUtilityMeterBaseNewExistingChange: (value: NewExistingValue) => void;
  onUtilityMeterBaseNameModelChange: (value: string) => void;
  selectedServiceFeedSource?: string;
  distanceFromUmCallout?: string;
  utilityMeterBaseNewExisting?: NewExistingValue;
  utilityMeterBaseNameModel?: string;
  isLoading: boolean;
  error?: string;
  isEnabled: boolean;
}


export const UtilitySelector: React.FC<UtilitySelectorProps> = ({
  onServiceFeedSourceSelect,
  onDistanceFromUmCalloutChange,
  onUtilityMeterBaseNewExistingChange,
  onUtilityMeterBaseNameModelChange,
  selectedServiceFeedSource,
  distanceFromUmCallout,
  utilityMeterBaseNewExisting,
  utilityMeterBaseNameModel,
  isLoading,
  error,
  isEnabled
}) => {

  const handleServiceFeedSourceSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onServiceFeedSourceSelect('');
      return;
    }
    onServiceFeedSourceSelect(option.label);
  };

  // Set default value for distance if empty
  React.useEffect(() => {
    if (isEnabled && (!distanceFromUmCallout || distanceFromUmCallout.trim() === '')) {
      onDistanceFromUmCalloutChange(UTILITY_CONFIG.DEFAULT_DISTANCE_TEXT);
    }
  }, [isEnabled, distanceFromUmCallout, onDistanceFromUmCalloutChange]);

  if (!isEnabled) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-gray-500 text-xs 3xl:text-sm">Select a task to configure Utility details</p>
      </div>
    );
  }

  return (
    <div className="bg-transparent p-0">
      <div className="space-y-4">
      {/* Row 1: Service Feed Source */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
            Service Feed Source
          </label>
          <SearchDropdown
            options={UTILITY_CONFIG.SERVICE_FEED_SOURCE_OPTIONS}
            onSelect={handleServiceFeedSourceSelect}
            placeholder="Choose service feed source..."
            isLoading={isLoading}
            error={error}
            className="h-9"
            value={selectedServiceFeedSource ? { 
              id: UTILITY_CONFIG.SERVICE_FEED_SOURCE_OPTIONS.find(opt => opt.label === selectedServiceFeedSource)?.id || '', 
              label: selectedServiceFeedSource 
            } : null}
            disabled={!isEnabled}
          />
        </div>
      </div>

      {/* Row 2: Distance from UM Callout */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
            Distance from UM Callout
          </label>
          <textarea
            value={distanceFromUmCallout || ''}
            onChange={(e) => onDistanceFromUmCalloutChange(e.target.value)}
            className="w-full px-2 py-1.5 text-xs 3xl:text-sm placeholder:text-[11px] 3xl:placeholder:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
            placeholder="Enter distance from UM callout..."
            disabled={!isEnabled}
            rows={3}
          />
        </div>
      </div>

      {/* Row 3: Utility Meter Base Name/Model */}
      <div className="grid grid-cols-12 gap-3 items-start">
        <div className="col-span-12">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                Utility Meter Base Name/Model
              </label>
              {isEnabled && (
                <NewExistingSegment
                  value={utilityMeterBaseNewExisting || null}
                  onChange={onUtilityMeterBaseNewExistingChange}
                  disabled={!isEnabled}
                />
              )}
            </div>
            <input
              type="text"
              value={utilityMeterBaseNameModel || ''}
              onChange={(e) => onUtilityMeterBaseNameModelChange(e.target.value)}
              className="w-full px-2 py-1.5 text-xs 3xl:text-sm placeholder:text-[11px] 3xl:placeholder:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
              placeholder="Enter utility meter base name/model..."
              disabled={!isEnabled || utilityMeterBaseNewExisting === NewExistingType.EXISTING}
            />
            {utilityMeterBaseNewExisting === NewExistingType.EXISTING && (
              <p className="text-xs 3xl:text-sm text-gray-500 mt-1">
                Name/Model not required for existing utility meter base
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};
